<script setup lang="ts">
/**
 * Public clients statement page — a single standalone URL (`/statement`,
 * routed with `meta: { standalone: true }` so `App.vue` renders it with no
 * app chrome: no sidebar, no nav, no auth-gated shell). Fetches
 * `GET /persons/clients/visits-report` (public, no session) for a date
 * range and shows every client with the business services they visited in
 * the period plus their latest balance (balance as of the 1st of the
 * current month plus payments made so far this month).
 *
 * Not one of the app's real tabs/views: it does not reuse `DesktopMain`,
 * `InfoViewLayout`, or any sidebar/nav component, only the shared
 * `DynamicTable`/cell-component building blocks, matching this app's
 * "DynamicTable everywhere" convention.
 *
 * No date range picker — this report always covers the previous full
 * calendar month, so the only inputs are the name search and Reload.
 */
import { computed, markRaw, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIsMobile } from '@/composables/useIsMobile'
import { fetchClientsVisitsReport } from '@/api/clientsVisitsReport'
import { majorToMinor } from '@/utils/majorToMinor'
import { formatMoney } from '@/utils/formatMoney'
import { formatDateTime } from '@/utils/formatDateTime'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import ClientStatementNameCell from '@/components/table/cell/ClientStatementNameCell.vue'
import ClientStatementServicesCell from '@/components/table/cell/ClientStatementServicesCell.vue'
import ClientStatementMobileCardCell from '@/components/table/cell/ClientStatementMobileCardCell.vue'

const { isMobile } = useIsMobile()
const { t } = useI18n()

interface StatementService {
  dateFormatted: string
  serviceName: string
  price: number
  priceFormatted: string
}

interface StatementRow {
  // One rendered table row: a client with their period statement, pre-formatted for display.

  clientId: string
  rowNumber: number
  name: string
  balanceFormatted: string
  balanceSign?: 'positive' | 'negative' | 'zero'
  businessServices: StatementService[]
}

/** `Date` → `YYYY-MM-DD` using local calendar fields (not `toISOString`, which would shift the date across a UTC day boundary for any non-UTC timezone). */
function isoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
function firstOfPreviousMonthIso(): string {
  const now = new Date()
  return isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1))
}
/** Day 0 of the current month rolls back to the last day of the previous month (JS Date semantics). */
function lastOfPreviousMonthIso(): string {
  const now = new Date()
  return isoDate(new Date(now.getFullYear(), now.getMonth(), 0))
}

// Always the full previous calendar month — no date picker, both
// start_date/end_date are inclusive on this endpoint (see person_endpoints.py).
const startDate = firstOfPreviousMonthIso()
const endDate = lastOfPreviousMonthIso()
const nameFilter = ref('')

const loading = ref(false)
const error = ref<string | undefined>()
const allRows = ref<Omit<StatementRow, 'rowNumber'>[]>([])
const hasLoaded = ref(false)

function formatPrice(amount: number): string {
  const minorUnits = majorToMinor(amount) ?? 0
  return formatMoney({ amount: minorUnits, includeCurrency: true }).formatted
}

function formatBalance(amount: number | null): {
  formatted: string
  sign?: 'positive' | 'negative' | 'zero'
} {
  if (amount === null) return { formatted: '–', sign: undefined }
  const minorUnits = majorToMinor(amount) ?? 0
  const formatted = formatMoney({ amount: minorUnits, includeCurrency: true }).formatted
  const sign = amount < 0 ? 'negative' : amount > 0 ? 'positive' : 'zero'
  return { formatted, sign }
}

function formatServiceDate(date: string): string {
  return formatDateTime({ date, dateStyle: 'medium' }).formattedDate
}

async function reload() {
  loading.value = true
  error.value = undefined
  try {
    const items = await fetchClientsVisitsReport(startDate, endDate)
    allRows.value = items
      // A client with no visits this period is only interesting if they owe
      // money (negative balance) — zero or positive balance plus no visits
      // means there's nothing to collect or flag, so exclude those rows.
      // Visits always keep the row, regardless of balance; an unknown (null)
      // balance with no visits is kept too, since it isn't confirmed >= 0.
      .filter((item) => {
        const hasVisits = item.business_services.length > 0
        const balanceIsNonNegative = item.latest_balance !== null && item.latest_balance >= 0
        return hasVisits || !balanceIsNonNegative
      })
      .map((item) => {
        const balance = formatBalance(item.latest_balance)
        return {
          clientId: item.client_id,
          name: item.name,
          balanceFormatted: balance.formatted,
          balanceSign: balance.sign,
          businessServices: item.business_services
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((bs) => ({
              dateFormatted: formatServiceDate(bs.date),
              serviceName: bs.business_service_name,
              price: bs.price,
              priceFormatted: formatPrice(bs.price),
            })),
        }
      })
    hasLoaded.value = true
  } catch (e) {
    error.value = e instanceof Error ? e.message : t('statement.loadError')
  } finally {
    loading.value = false
  }
}

// This public page must always render light, regardless of the viewer's OS
// dark-mode preference — `.force-light` on <html> opts it out of the app's
// `prefers-color-scheme: dark` styles (see tufte_theme.css).
onMounted(() => {
  document.documentElement.classList.add('force-light')
  void reload()
})

onUnmounted(() => {
  document.documentElement.classList.remove('force-light')
})

const rows = computed<StatementRow[]>(() => {
  const query = nameFilter.value.trim().toLowerCase()
  const filtered =
    query === ''
      ? allRows.value
      : allRows.value.filter((row) => row.name.toLowerCase().includes(query))
  return filtered.map((row, index) => ({ ...row, rowNumber: index + 1 }))
})

const columns = computed<Column<StatementRow>[]>(() => [
  {
    key: 'name',
    label: t('common.client'),
    component: markRaw(ClientStatementNameCell),
    props: (row) => ({
      rowNumber: row.rowNumber,
      name: row.name,
      balanceFormatted: row.balanceFormatted,
      balanceSign: row.balanceSign,
    }),
    width: '13rem',
  },
  {
    key: 'services',
    label: t('statement.colServicesVisited'),
    component: markRaw(ClientStatementServicesCell),
    props: (row) => ({ businessServices: row.businessServices }),
  },
])

/** Single wide column for the mobile table: each row's `<td>` is a full client "card" via ClientStatementMobileCardCell. */
const mobileColumns = computed<Column<StatementRow>[]>(() => [
  {
    key: 'card',
    label: t('common.client'),
    component: markRaw(ClientStatementMobileCardCell),
    props: (row) => ({
      name: row.name,
      balanceFormatted: row.balanceFormatted,
      balanceSign: row.balanceSign,
      businessServices: row.businessServices,
    }),
  },
])
</script>

<template>
  <div class="statement-page" :class="{ 'statement-page--mobile': isMobile }">
    <header v-if="!isMobile" class="statement-header">
      <div>
        <h1>{{ t('statement.title') }}</h1>
        <p v-if="hasLoaded" class="statement-period">
          {{ t('statement.period', { start: startDate, end: endDate }) }}
        </p>
      </div>
      <div class="statement-actions">
        <input
          v-model="nameFilter"
          type="search"
          class="statement-search"
          :placeholder="t('common.searchByName')"
          :aria-label="t('common.searchByName')"
        />
        <button
          type="button"
          class="statement-btn statement-btn--reload"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? t('common.loadingShort') : t('common.reload') }}
        </button>
      </div>
    </header>

    <template v-else>
      <header class="statement-mobile-header">
        <h1>{{ t('statement.title') }}</h1>
        <p v-if="hasLoaded" class="statement-period">{{ startDate }} – {{ endDate }}</p>
      </header>
      <div class="statement-actions statement-actions--mobile">
        <input
          v-model="nameFilter"
          type="search"
          class="statement-search statement-search--mobile"
          :placeholder="t('common.searchByName')"
          :aria-label="t('common.searchByName')"
        />
        <button
          type="button"
          class="statement-btn statement-btn--reload"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? t('common.loadingShort') : t('common.reload') }}
        </button>
      </div>
    </template>

    <p v-if="error" class="text-red-600">{{ error }}</p>

    <template v-if="!error">
      <div
        v-if="!isMobile"
        class="statement-scroll-wrap"
        :class="{ 'statement-scroll-wrap--loading': loading && rows.length > 0 }"
      >
        <DynamicTable
          v-if="rows.length > 0"
          :columns="columns as Column<unknown>[]"
          :data="rows"
          pinnable-header
          storage-key="clients-statement"
        />
        <p v-else-if="loading" class="text-tufte-muted statement-empty">
          {{ t('statement.loading') }}
        </p>
        <p v-else class="statement-empty">{{ t('statement.noMatch') }}</p>
      </div>

      <div
        v-else
        class="statement-cards"
        :class="{ 'statement-cards--loading': loading && rows.length > 0 }"
      >
        <DynamicTable
          v-if="rows.length > 0"
          :columns="mobileColumns as Column<unknown>[]"
          :data="rows"
          :pinnable-header="false"
        />
        <p v-else-if="loading" class="text-tufte-muted statement-empty">
          {{ t('statement.loading') }}
        </p>
        <p v-else class="statement-empty">{{ t('statement.noMatch') }}</p>
      </div>
    </template>
  </div>
</template>

<style scoped>
/*
 * Centered on the screen, not edge-to-edge: capped at max-width and
 * auto-margined like a normal page, so the desktop table doesn't stretch
 * fullscreen on wide viewports. Mobile overrides this back to full-bleed
 * below (`.statement-page--mobile`).
 */
.statement-page {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: 1.25rem 1.5rem;
}

.statement-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-tufte-border);
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

/* Global Tufte `h1` is essay-sized (3.2rem, 4rem top margin) — too large for a report title; shrink it to a normal page heading. */
.statement-header h1,
.statement-mobile-header h1 {
  font-size: var(--text-tufte-xl);
  margin-top: 0;
  margin-bottom: 0.3rem;
}

.statement-period {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-top: 0.2rem;
}

.statement-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.statement-search {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  outline: none;
  min-width: 12rem;
}

.statement-search:focus {
  border-color: color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
}

.statement-btn {
  appearance: none;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  border-radius: 6px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  border: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  color: var(--color-tufte-text);
}

.statement-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-tufte-border) 25%, var(--color-tufte-bg));
}

.statement-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.statement-scroll-wrap {
  overflow-x: auto;
  margin-top: 1rem;
}

/* Same `width: auto` reasoning as the mobile card list below — forces the two-column desktop table to fill the page instead of shrink-wrapping its content. */
.statement-scroll-wrap :deep(.dynamic-table) {
  width: 100%;
}

.statement-scroll-wrap--loading {
  opacity: 0.6;
}

.statement-empty {
  padding: 1.5rem 0.75rem;
  text-align: center;
  color: var(--color-tufte-muted);
  font-family: var(--font-tufte-sans);
  border: 1px solid var(--color-tufte-border);
  margin-top: 1rem;
}

/* Mobile layout: full-bleed (undoes the desktop centering above), title/actions stack full-width above a card list, same as StuffTab's mobile section. */
.statement-page--mobile {
  max-width: none;
  margin: 0;
  padding: 0;
}

.statement-mobile-header {
  padding: 0.75rem 1.25rem;
}

.statement-actions--mobile {
  padding: 0.75rem 1.25rem;
  flex-wrap: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
}

.statement-search--mobile {
  flex: 1;
  min-width: 0;
}

.statement-cards--loading {
  opacity: 0.6;
}

/*
 * DynamicTable's root `.dynamic-table` is `width: auto` by design (so
 * multi-column tables don't stretch an auto-width last column) — for this
 * single wide "card" column that means the table only grows as wide as one
 * card's content, leaving the page background exposed on the right. Same
 * fix as FinancesTab's mobile card list.
 */
.statement-cards :deep(.dynamic-table) {
  width: 100%;
}

.statement-page--mobile > .text-red-600,
.statement-page--mobile .statement-empty {
  margin-left: 1.25rem;
  margin-right: 1.25rem;
}
</style>
